import { router } from '@inertiajs/react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ClockIcon, CalendarIcon, XIcon, PlusIcon } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

interface Lead {
  id: number
  name: string
}

interface QuickAppointmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  leads: Lead[]
  defaultDate?: string
  businessType?: string
}

const appointmentSchema = z.object({
  lead_id: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
  date: z.string().min(1, 'Date is required'),
  hour: z.string().min(1, 'Hour is required'),
  minute: z.string().min(1, 'Minute is required'),
  duration: z.number().min(15, 'Duration must be at least 15 minutes').optional(),
  appointment_type: z.enum(['Diagnostic', 'Repair', 'Inspection', 'Maintenance']).optional(),
  technician: z.string().optional(),
  location_type: z.enum(['physical', 'google_meet', 'zoom']).optional(),
  location: z.string().optional(),
  notes: z.string().optional(),
})

type AppointmentFormData = z.infer<typeof appointmentSchema>

export function QuickAppointmentDialog({
  open,
  onOpenChange,
  leads,
  defaultDate,
  businessType = 'shop',
}: QuickAppointmentDialogProps) {
  const [customDuration, setCustomDuration] = useState(false)
  const [participants, setParticipants] = useState<string[]>([])
  const [participantInput, setParticipantInput] = useState('')
  const [participantError, setParticipantError] = useState('')

  // Get smart defaults
  const getDefaultTime = () => {
    const now = new Date()
    const nextHour = new Date(now.getTime() + 60 * 60 * 1000)
    nextHour.setMinutes(0, 0, 0)
    return {
      hour: nextHour.getHours().toString().padStart(2, '0'),
      minute: '00',
    }
  }

  const defaultTime = getDefaultTime()
  const today = defaultDate || new Date().toISOString().split('T')[0]

  const { register, handleSubmit, setValue, watch, reset, formState: { errors: formErrors, isSubmitting } } = useForm<AppointmentFormData>({
    resolver: zodResolver(appointmentSchema),
    defaultValues: {
      date: today,
      hour: defaultTime.hour,
      minute: defaultTime.minute,
      duration: 60,
      appointment_type: 'Diagnostic',
      title: '',
      location_type: 'physical',
      location: '',
    },
  })

  const addParticipant = () => {
    const email = participantInput.trim()
    if (!email) return

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setParticipantError('Please enter a valid email address')
      return
    }

    if (participants.includes(email)) {
      setParticipantError('This email is already added')
      return
    }

    setParticipants([...participants, email])
    setParticipantInput('')
    setParticipantError('')
  }

  const removeParticipant = (email: string) => {
    setParticipants(participants.filter((p) => p !== email))
  }

  const selectedDuration = watch('duration')

  const handleQuickDuration = (minutes: number) => {
    setValue('duration', minutes)
    setCustomDuration(false)
  }

  const onSubmit = (data: AppointmentFormData) => {
    // Determine location based on location_type
    let location = ''
    if (data.location_type === 'physical') {
      location = data.location || ''
    } else if (data.location_type === 'google_meet') {
      location = ''  // Google Calendar API will generate the Meet link
    } else if (data.location_type === 'zoom') {
      location = data.location || ''
    }

    // Send date, time, and duration separately to let Rails handle timezone properly
    const appointmentData = {
      lead_id: data.lead_id ? parseInt(data.lead_id) : null,
      title: data.title,
      date: data.date,
      time: `${data.hour}:${data.minute}`,
      duration: data.duration || 60,
      appointment_type: data.appointment_type || '',
      notes: data.notes || '',
      location: location,
      location_type: data.location_type || '',
      status: 'scheduled',
      technician: data.technician || '',
      participants: participants,
      business_type: businessType,
    }

    router.post('/appointments', { appointment: appointmentData }, {
      preserveScroll: true,
      preserveState: false,
      onSuccess: () => {
        onOpenChange(false)
        reset()
        setParticipants([])
        setParticipantInput('')
        setParticipantError('')
      },
    })
  }

  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const minutes = ['00', '15', '30', '45']

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Schedule New Appointment</DialogTitle>
          <DialogDescription>
            Create a new appointment quickly without leaving the calendar
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Participants - First field */}
          <div className="space-y-2">
            <Label htmlFor="participants">Additional Participants</Label>
            <div className="flex gap-2">
              <Input
                id="participants"
                type="email"
                placeholder="Enter email address"
                value={participantInput}
                onChange={(e) => {
                  setParticipantInput(e.target.value)
                  setParticipantError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addParticipant()
                  }
                }}
                data-testid="participant-email-input"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={addParticipant}
                data-testid="add-participant-button"
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            </div>
            {participantError && (
              <p className="text-sm text-destructive">{participantError}</p>
            )}
            {participants.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {participants.map((email) => (
                  <Badge key={email} variant="secondary" className="pr-1">
                    {email}
                    <button
                      type="button"
                      onClick={() => removeParticipant(email)}
                      className="ml-1 hover:bg-destructive/20 rounded-sm p-0.5"
                      data-testid={`remove-participant-${email}`}
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Add additional email addresses for participants (in addition to the lead)
            </p>
          </div>

          {/* Custom Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              type="text"
              placeholder="Enter appointment title"
              {...register('title')}
              data-testid="appointment-title-input"
            />
            {formErrors.title && (
              <p className="text-sm text-destructive">{formErrors.title.message}</p>
            )}
          </div>

          {/* Date and Time Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date">Date *</Label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="date"
                  type="date"
                  {...register('date')}
                  className="pl-9"
                  data-testid="appointment-date-input"
                />
              </div>
              {formErrors.date && (
                <p className="text-sm text-destructive">{formErrors.date.message}</p>
              )}
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label>Time *</Label>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <ClockIcon className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Select
                    value={watch('hour')}
                    onValueChange={(value) => setValue('hour', value)}
                  >
                    <SelectTrigger className="pl-9">
                      <SelectValue placeholder="Hour" />
                    </SelectTrigger>
                    <SelectContent>
                      {hours.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {parseInt(hour) === 0 ? '12 AM' : parseInt(hour) < 12 ? `${parseInt(hour)} AM` : parseInt(hour) === 12 ? '12 PM' : `${parseInt(hour) - 12} PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-muted-foreground">:</span>
                <Select
                  value={watch('minute')}
                  onValueChange={(value) => setValue('minute', value)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Min" />
                  </SelectTrigger>
                  <SelectContent>
                    {minutes.map((minute) => (
                      <SelectItem key={minute} value={minute}>
                        {minute}
                      </SelectItem>
))}
                  </SelectContent>
                </Select>
              </div>
              {(formErrors.hour || formErrors.minute) && (
                <p className="text-sm text-destructive">
                  {formErrors.hour?.message || formErrors.minute?.message}
                </p>
              )}
            </div>
          </div>

          {/* Lead and Appointment Type Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Lead Selection */}
            <div className="space-y-2">
              <Label htmlFor="lead_id">Lead</Label>
              <Select
                value={watch('lead_id')}
                onValueChange={(value) => setValue('lead_id', value)}
              >
                <SelectTrigger id="lead_id" data-testid="lead-select">
                  <SelectValue placeholder="Select a lead (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {leads.map((lead) => (
                    <SelectItem key={lead.id} value={lead.id.toString()}>
                      {lead.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.lead_id && (
                <p className="text-sm text-destructive">{formErrors.lead_id.message}</p>
              )}
            </div>

            {/* Appointment Type */}
            <div className="space-y-2">
              <Label htmlFor="appointment_type">Appointment Type</Label>
              <Select
                value={watch('appointment_type')}
                onValueChange={(value) => setValue('appointment_type', value as any)}
              >
                <SelectTrigger id="appointment_type" data-testid="appointment-type-select">
                  <SelectValue placeholder="Select type (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Diagnostic">Diagnostic</SelectItem>
                  <SelectItem value="Repair">Repair</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
              {formErrors.appointment_type && (
                <p className="text-sm text-destructive">{formErrors.appointment_type.message}</p>
              )}
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label>Duration</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={selectedDuration === 30 && !customDuration ? 'default' : 'outline'}
                onClick={() => handleQuickDuration(30)}
                className="flex-1 min-w-[100px]"
              >
                30 min
              </Button>
              <Button
                type="button"
                variant={selectedDuration === 60 && !customDuration ? 'default' : 'outline'}
                onClick={() => handleQuickDuration(60)}
                className="flex-1 min-w-[100px]"
              >
                1 hour
              </Button>
              <Button
                type="button"
                variant={selectedDuration === 120 && !customDuration ? 'default' : 'outline'}
                onClick={() => handleQuickDuration(120)}
                className="flex-1 min-w-[100px]"
              >
                2 hours
              </Button>
              <Button
                type="button"
                variant={customDuration ? 'default' : 'outline'}
                onClick={() => setCustomDuration(true)}
                className="flex-1 min-w-[100px]"
              >
                Custom
              </Button>
            </div>
            {customDuration && (
              <div className="mt-2">
                <Input
                  type="number"
                  placeholder="Duration in minutes"
                  min="15"
                  step="15"
                  value={selectedDuration}
                  onChange={(e) => setValue('duration', parseInt(e.target.value) || 60)}
                  data-testid="custom-duration-input"
                />
              </div>
            )}
            {formErrors.duration && (
              <p className="text-sm text-destructive">{formErrors.duration.message}</p>
            )}
          </div>

          {/* Optional Fields */}
          <div className="space-y-4 pt-4 border-t">
            <p className="text-sm text-muted-foreground font-medium">Optional Information</p>

            {/* Technician */}
            <div className="space-y-2">
              <Label htmlFor="technician">Technician</Label>
              <Input
                id="technician"
                type="text"
                placeholder="Assign a technician (optional)"
                {...register('technician')}
                data-testid="technician-input"
              />
            </div>

            {/* Location */}
            <div className="space-y-3">
              <Label>Location</Label>
              <RadioGroup
                value={watch('location_type') || 'physical'}
                onValueChange={(value) => {
                  setValue('location_type', value as any)
                  setValue('location', '')
                }}
                className="flex flex-row gap-4"
                data-testid="location-type-radio-group"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="physical" id="physical" data-testid="location-physical-radio" />
                  <Label htmlFor="physical" className="font-normal cursor-pointer">
                    Physical Location
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="google_meet" id="google_meet" data-testid="location-google-meet-radio" />
                  <Label htmlFor="google_meet" className="font-normal cursor-pointer">
                    Google Meet (Online)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="zoom" id="zoom" data-testid="location-zoom-radio" />
                  <Label htmlFor="zoom" className="font-normal cursor-pointer">
                    Zoom (Online)
                  </Label>
                </div>
              </RadioGroup>

              {watch('location_type') === 'physical' && (
                <Input
                  id="location"
                  type="text"
                  placeholder="Enter physical location or address"
                  {...register('location')}
                  data-testid="physical-location-input"
                />
              )}

              {watch('location_type') === 'google_meet' && (
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm text-muted-foreground">
                    A Google Meet link will be generated automatically when you save this appointment.
                  </p>
                </div>
              )}

              {watch('location_type') === 'zoom' && (
                <Input
                  id="location"
                  type="text"
                  placeholder="Enter Zoom meeting link"
                  {...register('location')}
                  data-testid="zoom-location-input"
                />
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any additional notes (optional)"
                rows={3}
                {...register('notes')}
                data-testid="appointment-notes-textarea"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false)
                reset()
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="save-appointment-button"
            >
              {isSubmitting ? 'Saving...' : 'Save Appointment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
